import Axios from "axios";
import { CY_SSO_BASE_URL } from "./constants";

const REFRESH_URL = `${CY_SSO_BASE_URL}api/login/validate/refresh`;

/**
 * Calls cy-sso token refresh (POST /api/login/validate/refresh).
 *
 * Resolves with `{ accessToken, refreshToken }` only on a real success
 * (HTTP 200 + `valid:true` + tokens). Throws on every failure:
 *   - 200 with `valid:false` (e.g. the session was deleted server-side) or
 *     missing tokens → Error tagged `refreshAuthFailed = true` (genuine).
 *   - HTTP error (401 invalid tokens, 500 server error, …) → the axios error,
 *     which carries `.response`.
 *   - Network / offline / DNS / timeout → axios error with NO `.response`.
 */
export const refreshTokens = async (tokenObj) => {
  const { data } = await Axios.post(
    REFRESH_URL,
    {
      accessToken: tokenObj.ACCESS_TOKEN,
      refreshToken: tokenObj.REFRESH_TOKEN,
    },
    { headers: { "auth-source": "cy-sso" } }
  );
  if (data && data.valid && data.accessToken && data.refreshToken) {
    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
  }
  if (data && data.recentDeletion) {
    // Lost refresh race — NOT a dead session. The refresh token we sent was
    // rotated away within the last minute, which means ANOTHER context of this
    // same session (a second tab, or the other app) refreshed first and holds
    // the live token pair. The caller should re-read localStorage/cookies and
    // adopt the winner's tokens instead of logging out (see the race recovery
    // in mfeRefreshCoordinator / createApiClient). Deliberately NOT
    // refreshAuthFailed.
    const raceErr = new Error("Refresh race lost — token was just rotated by another context");
    raceErr.refreshRaceLost = true;
    throw raceErr;
  }
  const err = new Error(data?.msg || "Refresh returned no valid tokens");
  err.refreshAuthFailed = true;
  throw err;
};

/**
 * Lost-race marker: the backend reported `recentDeletion` (this session was
 * refreshed by a sibling context seconds ago). Recoverable — never a logout.
 */
export const isRefreshRaceLost = (err) => err?.refreshRaceLost === true;

/**
 * Whether a refresh failure is GENUINE (→ log the user out) vs a transient
 * connectivity problem (→ keep the session; a later request retries the refresh).
 *
 * Genuine = the server actually responded (ANY status — 401 / 500 / …) OR the
 * refresh explicitly reported no valid tokens. NOT genuine = no `response` at all
 * (offline / DNS / CORS / timeout) — we must never log out a user who merely lost
 * their internet connection — and NOT a lost refresh race (`refreshRaceLost`,
 * thrown above with no `.response` and no `refreshAuthFailed`), which is
 * recovered by adopting the winning context's tokens.
 */
export const isGenuineRefreshFailure = (err) =>
  !!err?.response || err?.refreshAuthFailed === true;
